<template>
  <Grid
    class="schedule-a-grid"
    :pageable="pageable"
    :data-items="ingredients"
    :skip="skip"
    :take="take"
    :page-size="take"
    :total="total"
    :columns="columns"
    :loader="loading"
    :filter="filter"
    :column-menu="!loading"
    selected-field="selected"
    :sort="sort"
    :resizable="true"
    :reorderable="true"
    @columnresize="onColumnResize"
    @columnreorder="onColumnReorder"
    @filterchange="filterChangeHandler"
    @pagechange="pageChangeHandler"
    @selectionchange="changeSelection"
    @sortchange="sortChangeHandler"
    @rowclick="changeSelection"
    @headerselectionchange="changeHeaderSelection"
    data-cy="schedule-a-grid"
  >
    <template v-slot:columnMenu="{ props }">
      <ColumnMenu
        :column="props.column"
        :filterable="props.filterable"
        :filter="props.filter"
        :sortable="props.sortable"
        :sort="props.sort"
        :columns="columns"
        :getItems="getPropValues"
        @sortchange="(e: any) => props.onSortchange(e)"
        @filterchange="(e: any) => props.onFilterchange(e)"
        @closemenu="(e) => props.onClosemenu(e)"
        @contentfocus="(e) => props.onContentfocus(e)"
        @columnssubmit="onColumnsSubmit"
      />
    </template>
    <template v-slot:SelectCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        <input
          v-if="selectionAvailable(dataItem)"
          type="checkbox"
          v-model="dataItem[field]"
          @change="emit('selectionChanged', [dataItem])"
        />
      </td>
    </template>
    <template v-slot:DetailsCell="{ props: { dataItem } }">
      <td class="k-table-td">
        <input
          class="fa fa-external-link details-icon"
          type="image"
          alt="Detail Icon"
          @click.prevent="openDetails(dataItem)"
          :src="detailIconUrl"
          data-cy="details-action"
        />
      </td>
    </template>
    <template v-slot:RmcCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        <input
          class="rmc-input"
          type="text"
          :value="dataItem[field]"
          :disabled="!canModifyCarts"
          @input="(e: Event) => onRmcChanged((e.target as any)?.value, dataItem)"
          @focusout="onRmcFocusOutAsync(dataItem)"
          data-cy="rmc-input"
        />
      </td>
    </template>
    <template v-slot:DpmCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        {{ EnumDpm[dataItem[field]] }}
      </td>
    </template>
    <template v-slot:GroupCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        {{ convertNumber(dataItem[field]) }}
      </td>
    </template>
    <template v-slot:StipulationExistsCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        {{ dataItem[field] ? "Y" : "N" }}
      </td>
    </template>
    <template v-slot:PassoverCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        {{ dataItem[field] ? "Y" : "N" }}
      </td>
    </template>
    <template v-slot:UsedInOneStatusCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        {{
          enumHelper.splitByCapitalLetter(EnumUsedIn1Status[dataItem[field]])
        }}
      </td>
    </template>
    <template v-slot:PlantStatusCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        {{ EnumCompanyStatus[dataItem[field]] }}
      </td>
    </template>
  </Grid>
</template>

<script setup lang="ts">
import {
  Grid,
  type GridColumnProps,
  type GridColumnReorderEvent,
  type GridColumnResizeEvent,
  type GridFilterChangeEvent,
  type GridHeaderSelectionChangeEvent,
  type GridPageChangeEvent,
  type GridPagerSettings,
  type GridRowClickEvent,
  type GridSelectionChangeEvent,
  type GridSortChangeEvent,
} from "@progress/kendo-vue-grid";
import { ref, watchEffect } from "vue";
import type {
  CompositeFilterDescriptor,
  SortDescriptor,
} from "@progress/kendo-data-query";
import ColumnMenu from "@/shared/ui/inputs/ColumnMenu.vue";

import detailIconUrl from "@/assets/img/DetailIcon.png";
import { EnumDpm } from "@/Domain/EnumDpm";
import type { IngredientWithSelection } from "@/api/scheduleA";
import { useMainStore } from "@/stores/useMainStore";
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";
import { EnumGroup } from "@/Domain/EnumGroup";
import type {
  ItemChangedOptions,
  ItemSortChangedOptions,
} from "@/Domain/ItemChangedOptions";
import { convertNumber } from "@/Domain/EnumGroupConverter";
import { EnumUsedIn1Status } from "@/Domain/EnumUsedIn1Status";
import { EnumCompanyStatus } from "@/Domain/EnumCompanyStatus";
import enumHelper from "@/shared/helpers/enumHelper";
import { GridOptionsService } from "@/shared/services/GridOptionsService";
import { GridMenuActionType } from "@/Domain/GridMenuActionType";
import {
  getFilterOperatorsForEnum,
  getFilterOperatorsForNumber,
} from "@/shared/helpers/filtersHelper";

const main = useMainStore();
const notificationStore = useNotificationStore();

const props = defineProps<{
  ingredients: IngredientWithSelection[];
  loading: boolean;
  skip: number;
  pageSize: number;
  total: number;
  filter: CompositeFilterDescriptor;
  sort: SortDescriptor[];
  canModifyCarts: boolean;
  getPropValues: (
    column: GridColumnPropsExtended
  ) => Promise<IngredientWithSelection[]>;
}>();

const emit = defineEmits<{
  (e: "detailsClicked", ingredients: IngredientWithSelection): void;
  (e: "ingredientsChanged", options: ItemChangedOptions): void;
  (e: "selectionChanged", selectedIngredients: IngredientWithSelection[]): void;
  (e: "sortChanged", options: ItemSortChangedOptions): void;
  (e: "columnreorder", event: GridColumnReorderEvent): void;
  (e: "columnssubmit", columnsProps: GridColumnProps[]): void;
}>();

const gridName = "scheduleAGrid";

const skip = ref<number>(props.skip);
const take = ref<number>(props.pageSize);

const columns = ref<GridColumnPropsExtended[]>([
  {
    field: "selected",
    cell: "SelectCell",
    width: "50px",
    sortable: false,
    filterable: false,
    headerClassName: "hide-menu",
    hidden: !props.canModifyCarts,
    defaultHiddenValue: !props.canModifyCarts,
    hideInColumnMenu: true,
  },
  {
    cell: "DetailsCell",
    headerClassName: "hide-menu",
    sortable: false,
    filterable: false,
    width: "40px",
    hideInColumnMenu: true,
  },
  {
    title: "RMC",
    field: "Rmc",
    cell: "RmcCell",
    columnMenu: "columnMenu",
  },
  {
    title: "Ingredient Name/Number",
    field: "VueScheduleALabelName",
    columnMenu: "columnMenu",
    hideInColumnMenu: true,
  },
  {
    title: "Source Company",
    field: "DistributorCompanyName",
    columnMenu: "columnMenu",
  },
  {
    title: "Brand",
    field: "VueScheduleABrandName",
    columnMenu: "columnMenu",
  },
  {
    title: "Certifying Agency",
    field: "Symbol",
    columnMenu: "columnMenu",
  },
  {
    title: "DPM",
    field: "_Dpm",
    cell: "DpmCell",
    columnMenu: "columnMenu",
    width: "70px",
    allowedFilterOperators: getFilterOperatorsForEnum(),
  },
  {
    title: "Group",
    field: "VueScheduleAGroup",
    cell: "GroupCell",
    columnMenu: "columnMenu",
    width: "60px",
    allowedFilterOperators: getFilterOperatorsForNumber(),
  },
  {
    title: "Special Requirement",
    field: "VueScheduleAStipulationExists",
    cell: "StipulationExistsCell",
    columnMenu: "columnMenu",
    allowedFilterOperators: getFilterOperatorsForEnum(),
  },
  {
    title: "Passover Always",
    field: "_Pesach",
    cell: "PassoverCell",
    columnMenu: "columnMenu",
    allowedFilterOperators: getFilterOperatorsForEnum(),
  },
  {
    title: "Ingredient Status in Plant",
    field: "VueScheduleAUsedInOneStatus",
    cell: "UsedInOneStatusCell",
    columnMenu: "columnMenu",
    allowedFilterOperators: getFilterOperatorsForEnum(),
  },
  {
    title: "UKD ID",
    field: "UkdId",
    columnMenu: "columnMenu",
  },
  {
    title: "Used in Plant",
    field: "PlantName",
    columnMenu: "columnMenu",
  },
  {
    title: "Plant Status",
    field: "_PlantStatus",
    cell: "PlantStatusCell",
    columnMenu: "columnMenu",
  },
  {
    title: "Merch ID",
    field: "MerchandiseId",
    columnMenu: "columnMenu",
    width: "70px",
    allowedFilterOperators: getFilterOperatorsForNumber(),
  },
]);

let pageSizes = [25, 50, 100, 200, 500];
if (!pageSizes.includes(take.value)) pageSizes.push(take.value);
pageSizes = pageSizes.sort((a, b) => a - b);
const pageable = ref<GridPagerSettings>({
  buttonCount: 5,
  info: true,
  type: "numeric",
  pageSizes: pageSizes,
  previousNext: true,
});

GridOptionsService.loadGridOptions(gridName, columns.value);

const filterChangeHandler = function (e: GridFilterChangeEvent) {
  skip.value = 0;
  emit("ingredientsChanged", {
    skip: 0,
    take: take.value,
    filterEvent: e,
  });
};

const sortChangeHandler = function (e: GridSortChangeEvent) {
  const descriptors = e.sort as SortDescriptor[];
  if (!descriptors) return;
  skip.value = 0;
  emit("sortChanged", {
    field: descriptors[0].field,
    ascending: descriptors[0].dir === "asc",
  });
};

const pageChangeHandler = function (event: GridPageChangeEvent) {
  skip.value = event.page.skip;
  take.value = event.page.take;
  emit("ingredientsChanged", {
    skip: event.page.skip,
    take: event.page.take,
    filterEvent: null,
  });
};

const selectionAvailable = function (dataItem: IngredientWithSelection) {
  return !(
    dataItem.VueScheduleAGroup == EnumGroup.Six &&
    (dataItem.VueScheduleAUsedInOneStatus == EnumUsedIn1Status.Allowed ||
      dataItem.VueScheduleAUsedInOneStatus == EnumUsedIn1Status.NotAllowed)
  );
};

const changeSelection = function (
  event: GridSelectionChangeEvent | GridRowClickEvent
) {
  // if clicked on the details icon or RMC input or canModifyCarts is false,
  // or selection is not available, don't change the selection
  if (!props.canModifyCarts) return;

  const className = event.event?.target?.className;

  if (!className) {
    return; // return in this case . why isnt there any event info? //mask for bug #23109
  } else if (
    className.includes &&
    (className.includes("details-icon") || className.includes("rmc-input"))
  ) {
    //console.log('class name: ' + className)
    return;
  }

  const ingredient = event.dataItem as IngredientWithSelection;
  if (!ingredient || !selectionAvailable(ingredient)) return;
  ingredient.selected = !ingredient.selected;
  emit("selectionChanged", [ingredient]);
};

const changeHeaderSelection = function (event: GridHeaderSelectionChangeEvent) {
  const checked = event.event.target.checked;
  props.ingredients.forEach((ingredient) => {
    ingredient.selected = checked && selectionAvailable(ingredient);
  });
  emit("selectionChanged", props.ingredients);
};

const onColumnsSubmit = function (columnsProps: GridColumnProps[]) {
  if (columnsProps) {
    columns.value = columnsProps;
    GridOptionsService.saveVisibleColumns(gridName, columnsProps);
  }
  console.debug("ScheduleAGrid.vue, onColumnSubmit", columnsProps);

  emit("columnssubmit", columnsProps);
};

const onColumnResize = function (e: GridColumnResizeEvent) {
  const col = GridOptionsService.getResizedColumn(e, columns.value);
  if (col.title)
    GridOptionsService.saveColumnWidth(gridName, col.title, e.newWidth);
};

const onColumnReorder = function (e: GridColumnReorderEvent) {
  GridOptionsService.setSelectDetailsColumnOrder(e);
  columns.value = e.columns;
  GridOptionsService.saveColumnsOrder(gridName, e.columns);
  emit("columnreorder", e);
};

const openDetails = function (ingredient: IngredientWithSelection) {
  emit("detailsClicked", ingredient);
};

const resetColumnSelection = function () {
  columns.value.forEach((col) => {
    col.hidden = col.defaultHiddenValue ?? false;
  });
  GridOptionsService.clearVisibleColumns(gridName);
};

const resetColumnSizes = function (showNotification = true) {
  GridOptionsService.clearColumnWidths(gridName);
  if (showNotification) {
    notificationStore.notify({
      type: NotificationTypeEnum.success,
      message:
        "Column sizes reset successfully. Please refresh the page to see the changes.",
      duration: 5000,
    });
  }
};

const resetColumnOrder = function () {
  columns.value.forEach((col) => {
    col.orderIndex = undefined;
  });
  GridOptionsService.clearColumnsOrder(gridName);
};

const resetAllSettings = function () {
  resetColumnSelection();
  resetColumnSizes(false);
  resetColumnOrder();

  notificationStore.notify({
    type: NotificationTypeEnum.success,
    message:
      "All settings reset successfully. Please refresh the page to see the changes.",
    duration: 5000,
  });
};

const executeAction = function (actionType: GridMenuActionType) {
  switch (actionType) {
    case GridMenuActionType.ResetColumnSelection:
      resetColumnSelection();
      break;
    case GridMenuActionType.ResetColumnSizes:
      resetColumnSizes();
      break;
    case GridMenuActionType.ResetColumnOrder:
      resetColumnOrder();
      break;
    case GridMenuActionType.ResetAllSettings:
      resetAllSettings();
      break;
  }
};

const onRmcChanged = function (
  value: string,
  ingredient: IngredientWithSelection
) {
  ingredient.newRmc = value;
  console.log("ScheduleAGrid.vue, onRmcChanged", value, ingredient);
};

const onRmcFocusOutAsync = async function (
  ingredient: IngredientWithSelection
) {
  if (
    ingredient.newRmc == undefined ||
    ingredient.newRmc == null ||
    ingredient.newRmc == ingredient.Rmc ||
    !props.canModifyCarts
  )
    return;

  const rmcPromise = api.get("VueModifyRmc", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    usedIn1Id: ingredient.UsedInOneId.toString(),
    newRmc: ingredient.newRmc,
  });

  await handleResponsePromiseAsync(
    rmcPromise,
    (success: boolean) => {
      if (success) {
        ingredient.Rmc = ingredient.newRmc;
        notificationStore.notify({
          type: NotificationTypeEnum.success,
          message: "RMC updated successfully",
          duration: 5000,
        });
      } else {
        ingredient.newRmc = ingredient.Rmc;
        notificationStore.notify({
          type: NotificationTypeEnum.error,
          message: "Failed to update RMC",
          duration: 5000,
        });
      }
    },
    "ScheduleAGrid.vue",
    "onRmcFocusOutAsync",
    () => {
      notificationStore.notify({
        type: NotificationTypeEnum.error,
        message: "Failed to update RMC",
        duration: 5000,
      });
    }
  );
};

defineExpose({
  executeAction,
});

watchEffect(() => {
  skip.value = props.skip;
});

watchEffect(() => {
  take.value = props.pageSize;
});
</script>

<style scoped lang="scss">
.k-grid {
  .k-pager {
    .k-input-inner {
      padding: 0.375rem 0.375rem;
    }
  }

  .rmc-input {
    width: 100%;
  }
}

.schedule-a-grid {
  min-height: 400px;
  height: 800px;
}
</style>
